/**
 * Générateur — Il représente graphiquement une fonction linéaire, une
 * fonction affine.
 * Attendu ID : 226 | Niveau : 3ème | Domaine : Fonctions (d13, O14)
 *
 * Source doc prof (B3S6). Plan auto-validé 2026-07-16 (TODO §6) :
 * « points » (placer les points qui définissent la droite —
 * placer_points_2d 4 quadrants, format canonique strict), « nature » (QCM :
 * droite par l'origine / droite / courbe). Adaptation : « trace la droite »
 * devient « place les deux points qui la définissent » (l'app place des
 * points, elle ne trace pas de droites).
 */

import { randomChoice, randInt, tex, makeQcm } from '../../../utils';
import { frNb, decFrag, fmtAffine, NOMS_FN } from './fonctions';

const ATTENDU_ID = 226;
const L_POINTS = 'Placer les points qui définissent la droite';
const L_NATURE = 'Reconnaître la représentation graphique';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « points » — placer_points_2d ═════════

const genPointsPlacer = () => {
    const nom = randomChoice(NOMS_FN);
    const lineaire = randInt(0, 1) === 1;
    let a; let b; let x1; let y1; let x2; let y2;
    for (let essai = 0; essai < 60; essai++) {
        a = randomChoice([-3, -2, -1, 1, 2, 3]);
        b = lineaire ? 0 : randomChoice([-4, -3, -2, -1, 1, 2, 3, 4]);
        x1 = 0; y1 = b;                                    // ordonnée à l'origine
        x2 = randomChoice([1, 2, 3, -2]);
        y2 = a * x2 + b;
        if (Math.abs(y2) <= 8 && (x1 !== x2 || y1 !== y2) && !(lineaire && x2 === 0)) break;
        y2 = 99;
    }
    if (Math.abs(y2) > 8) { a = 2; b = -1; x1 = 0; y1 = -1; x2 = 2; y2 = 3; }
    const brA = `${x1};${y1}`;
    const brB = `${x2};${y2}`;
    const payload = {
        type: 'association',
        // L'ORDONNÉE de B n'est plus donnée : la consigne fournissait A et B
        // complets, l'élève n'avait plus qu'à reporter des coordonnées.
        texte: `<p>On veut représenter la fonction ${tex(`${nom} : x \\mapsto ${fmtAffine(a, b)}`)} — `
            + 'sa représentation est une droite.</p>'
            // « Calcule f(0) et f(x2) pour trouver les ordonnées » donnait le
            // calcul à faire ; « d'abscisse 0 / d'abscisse x2 » suffit.
            + `<p><b>Place le point A d'abscisse 0, puis le point B d'abscisse ${frNb(x2)}</b>, `
            + 'qui suffisent à tracer cette droite.</p>',
        contenu: {
            subType: 'placer_points_2d',
            repere: { xMin: -4, xMax: 4, yMin: -8, yMax: 8, xStep: 1, yStep: 1, grille: true },
            pointsToPlace: ['A', 'B'],
            bonne_reponse: JSON.stringify({ A: brA, B: brB }),
        },
        elements_additionnels: null,
        niveau: '3eme',
        matiere: 'maths',
        attendu: ATTENDU_ID,
        _meta: { label: L_POINTS, params: { nom, a, b, x1, y1, x2, y2 } },
    };
    return payload;
};

// ═════════ « nature » — QCM ═════════

const genNatureQcm = () => {
    const nom = randomChoice(NOMS_FN);
    const cas = randomChoice(['lineaire', 'affine', 'carre']);
    let expr;
    if (cas === 'lineaire') expr = fmtAffine(randomChoice([-4, -3, -2, 2, 3, 4]), 0);
    else if (cas === 'affine') expr = fmtAffine(randomChoice([-3, -2, 2, 3]), randomChoice([-5, -3, -1, 1, 2, 4]));
    else expr = `${decFrag(randomChoice([2, 3, -2]))}x^{2}`;
    const chLin = 'Une droite qui passe par l\'origine';
    const chAff = 'Une droite qui ne passe pas par l\'origine';
    const chCourbe = 'Une courbe qui n\'est pas une droite';
    const correct = cas === 'lineaire' ? chLin : (cas === 'affine' ? chAff : chCourbe);
    const payload = makeQcm([chLin, chAff, chCourbe], correct,
        `<p>On pose ${tex(`${nom} : x \\mapsto ${expr}`)}.</p>`
        + `<p><b>Quelle est la nature de la représentation graphique de ${nom} ?</b></p>`,
        opts(L_NATURE));
    payload._meta.params = { nom, cas, expr };
    return payload;
};

const GENERATORS = {
    points_placer_points_2d: genPointsPlacer,
    nature_qcm_unique: genNatureQcm,
};
const FORMAT_LABELS = { placer_points_2d: 'Placer (repère)', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'points', label: L_POINTS, formatsList: buildFormats('points') },
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
